import { yupResolver } from '@hookform/resolvers/yup'
import { filter, find, isEqual, map } from 'lodash-es'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { PageTitle } from '@/components'
import { Button, Checkbox, DateTimePicker, Input, Modal } from '@/components/ui'
import { cn } from '@/lib/utils'
import { getTranslation } from '../../../account/services/auth'
import { RewardTicket, Vouchers, VouchersPayload } from '../../../common/models'
import {
  VoucherDiscountType,
  voucherTypes,
} from '../../../common/models/rewards'
import { TicketCategory, TicketList } from '../../../common/models/ticket'
import FormGroup from '../../../core/components/FormGroup'
import { formatDateTime } from '../../../core/constants/times'
import {
  getAllCategories,
  getTicketsByCategoryId,
} from '../../../core/services/tickets'
import translate from '../../../core/services/translate'
import {
  deleteVouchers,
  downloadVoucherSingle,
  generateCode,
  getVouchers,
  saveVoucher,
  updateVoucher,
} from '../../../core/services/voucher'
import { useAppSelector } from '../../../store'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { InfoIcon } from 'lucide-react'
import SelectField from '@/components/select-field'
import Loader from '@/common/components/Loader'

export interface IVoucherValues {
  code: string
  label?: string
  exportType?: {
    pdf?: boolean
    png?: boolean
    svg?: boolean
    csv?: boolean
  }
  discountType?: 'PERCENTAGE' | 'AMOUNT'
  discountValue?: number
  tickets?: RewardTicket[]
  publishDate?: string
  expirationDate?: string
  voucherLimit?: number | null
  unlimited?: boolean | null
  userLimit?: number | null
  userUnlimited?: boolean | null
  dubaiIntegration?: boolean
}

export interface VoucherCreateGeneralModalProps {
  voucher: Vouchers | undefined
  editMode: boolean
  setEditMode?: (e: boolean) => void
  showCreateModal?: boolean
  setShowCreateModal?: (s: boolean) => void
  onVoucherCreated?: () => void
  dubaiIntegration: boolean
  currency: string
}

interface IIndexTicket {
  [index: number]: TicketList[]
}

export const VoucherCreateGeneralModal = (
  props: VoucherCreateGeneralModalProps,
): JSX.Element => {
  const {
    voucher,
    editMode,
    setEditMode,
    showCreateModal,
    setShowCreateModal,
    onVoucherCreated,
    dubaiIntegration,
    currency,
  } = props

  const allDiscountTypes = ['PERCENTAGE', 'AMOUNT']
  const { festivals } = useAppSelector((s) => s.app)
  const { editionId, festivalName } = useParams()
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [removeTicket, setRemoveTicket] = useState<number>(0)
  const [active, setActive] = useState<boolean>()
  const [deleteModal, setDeleteModal] = useState<boolean>(false)
  const [ticketCategoriesData, setTicketCategoriesData] = useState<
    TicketCategory[]
  >([])
  const [conflictVoucher, setConflictVoucher] = useState<Vouchers>()
  const [selectedTicketIds, setSelectedTicketIds] = useState<string[]>([])
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const [submitting, setSubmitting] = useState<boolean>(false)

  const [tickets, setTickets] = useState<IIndexTicket>({})

  const language = getTranslation()

  const getData = useCallback(async () => {
    if (!editionId) return
    try {
      const { data } = await getAllCategories({ size: 1000 })
      setTicketCategoriesData(data.content)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }, [editionId])

  useEffect(() => {
    if (showCreateModal) {
      getData()
    }
  }, [showCreateModal, getData])

  useEffect(() => {
    if (voucher) {
      setValue('label', voucher?.label)
      setValue('code', voucher.code)
      setValue('discountType', voucher.discountType)
      setValue('discountValue', voucher.amount)
      if (voucher.tickets && voucher.tickets.length > 0) {
        replace(
          map(voucher.tickets, (ticket) => ({
            ticketType: ticket.ticket.category.id,
            selectedTicket: ticket.ticket.ticket.id,
            appliesToCount:
              ticket.appliesToCount && ticket.appliesToCount > -1
                ? ticket.appliesToCount
                : null,
            appliesToCountUnlimited: !(
              ticket.appliesToCount && ticket.appliesToCount > -1
            ),
            dubaiOverwritePriceTypeCode:
              ticket.ticket.dubaiOverwritePriceTypeCode,
          })),
        )
        map(voucher.tickets, (ticket, index) => {
          getCategoryTickets(index, ticket.ticket.category.id)
          setValue(`tickets.${index}.selectedTicket`, ticket.ticket.ticket.id)
        })
      }
      setValue('publishDate', formatDateTime(voucher.publishDate))
      setValue('expirationDate', formatDateTime(voucher.expiryDate))
      setValue('voucherLimit', voucher.voucherLimit)
      setValue('unlimited', voucher.unlimited)
      setValue('userLimit', voucher.userLimit)
      setValue('userUnlimited', voucher.userUnlimited)
      setValue('exportType.pdf', voucher.exportPDF)
      setValue('exportType.png', voucher.exportPNG)
      setValue('exportType.svg', voucher.exportSVG)
      setValue('exportType.csv', voucher.exportCSV)
      setActive(voucher.status === 'ACTIVE')
    }

    return () => {
      reset()
      setActive(false)
    }
  }, [voucher, editMode])

  useEffect(() => {
    setValue('dubaiIntegration', dubaiIntegration, {
      shouldValidate: true,
    })
  }, [dubaiIntegration])

  const schemaObject = {
    code: yup.string().required(),
    label: yup.string().required(),
    exportType: yup
      .object()
      .shape({
        pdf: yup.boolean().optional(),
        png: yup.boolean().optional(),
        svg: yup.boolean().optional(),
        csv: yup.boolean().optional(),
      })
      .optional(),
    discountType: yup
      .mixed<VoucherDiscountType>()
      .oneOf(['PERCENTAGE', 'AMOUNT']),
    discountValue: yup.number(),
    tickets: yup
      .array()
      .of(
        yup.object().shape({
          ticketType: yup.string().required(),
          selectedTicket: yup.string().required(),
          appliesToCount: yup.number().nullable().optional(),
          appliesToCountUnlimited: yup.boolean().nullable().optional(),
          dubaiOverwritePriceTypeCode: yup.string().when('dubaiIntegration', {
            is: (val: boolean) => (val ? true : false),
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.nullable().optional(),
          }),
        }),
      )
      .required(),
    publishDate: yup.string().required(),
    expirationDate: yup.string().required(),
    voucherLimit: yup.number().when('unlimited', {
      is: (val: boolean) => (val ? true : false),
      then: (schema) =>
        schema
          .nullable()
          .optional()
          .transform((val) => (!isNaN(val) ? val : null)),
      otherwise: (schema) =>
        schema.required().transform((val) => (!isNaN(val) ? val : null)),
    }),
    unlimited: yup.boolean().nullable().optional(),
    userLimit: yup.number().when('userUnlimited', {
      is: (val: boolean) => (val ? true : false),
      then: (schema) =>
        schema
          .nullable()
          .optional()
          .transform((val) => (!isNaN(val) ? val : null)),
      otherwise: (schema) =>
        schema.required().transform((val) => (!isNaN(val) ? val : null)),
    }),
    userUnlimited: yup.boolean().nullable().optional(),
    dubaiIntegration: yup.boolean().optional(),
  }

  const schema = yup.object().shape(schemaObject)

  const downloadVoucher = async (id: string, type: 'PNG' | 'PDF' | 'SVG') => {
    try {
      const response = await downloadVoucherSingle(id, type)
      const href = URL.createObjectURL(response.data)

      const link = document.createElement('a')
      link.href = href
      link.setAttribute('download', `${id}.${type}`)
      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      URL.revokeObjectURL(href)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<IVoucherValues>({
    mode: 'onChange',
    resolver: yupResolver<IVoucherValues>(schema),
    defaultValues: {
      tickets: [
        {
          ticketType: '',
          selectedTicket: '',
          appliesToCount: null,
          appliesToCountUnlimited: true,
        },
      ],
    },
  })

  const handleClose = () => {
    setActive(false)
    setShowCreateModal?.(false)
    setEditMode?.(false)
    reset()
  }

  const submit = async (values: IVoucherValues) => {
    try {
      const payload: VouchersPayload = {
        code: values.code,
        discountType: values.discountType as VoucherDiscountType,
        amount: values.discountValue || 0,
        publishDate: values.publishDate
          ? new Date(values.publishDate).getTime()
          : null,
        expiryDate: values.expirationDate
          ? new Date(values.expirationDate).getTime()
          : null,
        tickets: map(values.tickets, (ticket, index) => ({
          ticket: {
            festivalEdition: {
              id: editionId as string,
              name: festivalName as string,
            },
            ticket: {
              id: ticket.selectedTicket,
              name: tickets[index]
                ? find(tickets[index], {
                    id: ticket.selectedTicket,
                  })?.name || ''
                : '',
            },
            category: {
              id: ticket.ticketType,
              name:
                find(ticketCategoriesData, { id: ticket.ticketType })?.name ||
                '',
            },
            ...(ticket.dubaiOverwritePriceTypeCode
              ? {
                  dubaiOverwritePriceTypeCode:
                    ticket.dubaiOverwritePriceTypeCode,
                }
              : {}),
          },
          appliesToCount: ticket.appliesToCountUnlimited
            ? null
            : ticket.appliesToCount || null,
        })),
        userLimit: values.userLimit || null,
        userUnlimited: values.userUnlimited || false,
        voucherLimit: values.voucherLimit || null,
        unlimited: values.unlimited || false,
        festivalId: find(festivals, { id: editionId })?.id as string,
        pngEnabled: values.exportType?.png || false,
        svgEnabled: values.exportType?.svg || false,
        pdfEnabled: values.exportType?.pdf || false,
        csvEnabled: values.exportType?.csv || false,
        id: voucher?.id,
        label: values.label,
        type: voucherTypes[0].value,
        users: [],
      }
      setSubmitting(true)
      const { data, status } = voucher?.id
        ? await updateVoucher(payload)
        : await saveVoucher(payload)
      if (status === 200) {
        if (watchExportType?.pdf) {
          await downloadVoucher(data.id, 'PDF')
        }
        if (watchExportType?.png) {
          await downloadVoucher(data.id, 'PNG')
        }
        if (watchExportType?.svg) {
          await downloadVoucher(data.id, 'SVG')
        }
        onVoucherCreated?.()

        handleClose()
      }
    } catch (e) {
      if (e instanceof Error) {
        const status = (e as { response?: { status?: number } })?.response
          ?.status
        setIsError(true)
        setShowMessageBox(true)
        setMessage(
          status === 409 ? 'The voucher code already exists' : e.message,
        )
        setErrorStatus(status)
      } else {
        console.error(e)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = () => {
    setDeleteModal(true)
  }

  const deleteVoucher = async () => {
    setDeleteLoading(true)
    try {
      const { status } = await deleteVouchers({ ids: [voucher?.id as string] })
      if (status === 204) {
        handleClose()
        setDeleteConfirmationModal(false)
        onVoucherCreated?.()
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Delete Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setDeleteLoading(false)
    }
  }

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'tickets',
  })

  const watchExportType = watch('exportType')
  const discountType = watch('discountType')
  const userUnlimited = watch('userUnlimited')
  const unlimited = watch('unlimited')

  const ticketCategory = watch('tickets')

  const publishDateWatch = watch('publishDate')
  const expirationDateWatch = watch('expirationDate')
  const codeWatch = watch('code')

  useEffect(() => {
    ;(async () => {
      if (codeWatch && publishDateWatch && expirationDateWatch) {
        try {
          const { data, status } = await getVouchers({
            size: 1000,
            startDate: new Date(publishDateWatch).valueOf(),
            endDate: new Date(expirationDateWatch).valueOf(),
            requestType: 'GENERAL',
          })
          if (status === 200) {
            const conflict = filter(data.content, (d) =>
              isEqual(d.code, codeWatch),
            )
            if (
              conflict.length > 0 &&
              find(
                selectedTicketIds,
                (t) =>
                  find(
                    conflict[0].tickets,
                    (ticket) => ticket?.ticket?.ticket?.id === t,
                  ) !== undefined,
              )
            ) {
              setConflictVoucher(conflict[0])
            } else {
              setConflictVoucher(undefined)
            }
          }
        } catch (e) {
          if (e instanceof Error) {
            setIsError(true)
            setShowMessageBox(true)
            setMessage(e.message)
            setErrorStatus(
              (e as { response?: { status?: number } })?.response?.status,
            )
          } else {
            console.error(e)
          }
        }
      }
    })()
  }, [publishDateWatch, expirationDateWatch, codeWatch, selectedTicketIds])

  const getCategoryTickets = async (index: number, category?: string) => {
    setValue(`tickets.${index}.selectedTicket`, '')
    try {
      let fetched: TicketList[] | undefined
      if (category) {
        const { data } = await getTicketsByCategoryId(
          category,
          { size: 1000 },
          false,
        )
        fetched = data.content
      } else if (ticketCategory && ticketCategory[index]?.ticketType) {
        const { data } = await getTicketsByCategoryId(
          ticketCategory[index]?.ticketType,
          { size: 1000 },
          false,
        )
        fetched = data.content
      }

      setTickets((prev) => {
        const base = fetched ?? prev[index]
        if (!base) return prev
        const sorted = [...base].sort((a, b) => {
          if (a.published === b.published) {
            if (a.published) {
              return a.ordinal - b.ordinal
            } else {
              if (a.publishTimestamp === b.publishTimestamp) {
                return a.ordinal - b.ordinal
              } else {
                return a.publishTimestamp < b.publishTimestamp ? -1 : 1
              }
            }
          } else {
            return a.published < b.published ? 1 : -1
          }
        })
        return { ...prev, [index]: sorted }
      })
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const handleGenerateCLicked = async () => {
    if (active) {
      navigator.clipboard.writeText(voucher?.code || '')
    } else {
      const res = await generateCode()
      if (res.status === 200) {
        setValue('code', res.data, {
          shouldValidate: true,
        })
        setActive(true)
      }
    }
  }

  const generateClassBasedOnPublish = (ticket: TicketList | undefined) => {
    if (!ticket) return undefined

    if (ticket.published) {
      return 'text-green-500'
    } else {
      if (ticket.publishTimestamp - new Date().valueOf() <= 0) {
        return 'text-red-500'
      } else {
        return 'text-yellow-500'
      }
    }
  }

  const generateSelectClassBasedOnPublish = (
    ticket: RewardTicket | undefined,
    index: number,
  ) => {
    if (!ticket) return undefined
    const t = find(tickets[index], (tic) => tic.id === ticket.selectedTicket)
    if (!t) return undefined

    if (t.published) {
      return 'text-green-500'
    } else {
      if (t.publishTimestamp - new Date().valueOf() <= 0) {
        return 'text-red-500'
      } else {
        return 'text-yellow-500'
      }
    }
  }

  const deleteFunc = () => {
    remove(removeTicket)
  }

  const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <div className="mb-5 text-2xl font-bold leading-none text-monochrome-1">
      {children}
    </div>
  )

  return (
    <>
      {deleteLoading && <Loader />}
      <Modal
        open={showCreateModal || false}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="xl"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle
              title="Vouchers"
              subtitle={
                editMode
                  ? active
                    ? 'Edit Voucher'
                    : 'View Voucher'
                  : 'Add New Voucher'
              }
            />
          </div>
        }
        footer={
          <div className="flex w-full justify-between items-center gap-2 border-t border-monochrome-120 pt-4">
            {editMode ? (
              <Button
                onClick={handleDelete}
                variant="secondary"
                className="mr-auto bg-accent-3 text-white hover:bg-accent-3/90"
              >
                Delete
              </Button>
            ) : (
              <div className="min-w-10" />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={handleClose} type="button" variant="secondary">
                {editMode ? 'Close' : 'Cancel'}
              </Button>
              <Button
                form="voucher-form"
                type="submit"
                variant="primary"
                loading={submitting}
                disabled={submitting}
              >
                {submitting
                  ? 'Submitting ...'
                  : watchExportType?.csv ||
                    watchExportType?.pdf ||
                    watchExportType?.png ||
                    watchExportType?.svg
                  ? 'Save & Download QR'
                  : 'Save'}
              </Button>
            </div>
          </div>
        }
      >
        <SectionTitle>General Information</SectionTitle>
        <form
          id="voucher-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <FormGroup label="Voucher Name" error={errors?.label?.message}>
            <Input
              isInvalid={!!errors?.label?.message}
              {...register('label')}
              type="text"
              disabled={editMode && !active}
              placeholder="Enter Name"
            />
          </FormGroup>

          <FormGroup label="Voucher Code" error={errors?.code?.message}>
            <div className="flex gap-2 w-full">
              <Input
                isInvalid={!!errors?.code?.message}
                {...register('code')}
                type="text"
                disabled={editMode && !active}
                placeholder="Enter Voucher Code"
                className="flex-1"
              />
              <Button
                variant="secondary"
                disabled={editMode && !active}
                onClick={handleGenerateCLicked}
                className="h-[44px]"
                type="button"
              >
                {active ? 'Copy' : 'Generate'}
              </Button>
            </div>
          </FormGroup>

          {conflictVoucher && (
            <div className="p-3 flex items-center gap-2 w-full bg-monochrome-5-50 border border-monochrome-120 rounded-[10px] mb-4">
              <InfoIcon className="w-4 h-4 text-accent-4 shrink-0" />
              <div className="flex flex-col">
                <span className="text-body-medium text-monochrome-1">
                  This voucher already exists, see information below:
                </span>
                <span className="text-body-medium text-monochrome-1">
                  <strong>Discount Type:</strong> {conflictVoucher.discountType}{' '}
                  - {conflictVoucher.amount}{' '}
                  {conflictVoucher.discountType ? '%' : currency}
                </span>
                <span className="text-body-medium text-monochrome-1">
                  <strong>Publish Date:</strong>{' '}
                  {formatDateTime(conflictVoucher.publishDate)}
                </span>
                <span className="text-body-medium text-monochrome-1">
                  <strong>Expiration Date:</strong>{' '}
                  {formatDateTime(conflictVoucher.expiryDate)}
                </span>
                <div className="flex gap-2">
                  <span className="text-body-medium text-monochrome-1 min-w-[138px]">
                    <strong>Applied for Tickets:</strong>
                  </span>
                  <div className="flex flex-col">
                    <span className="text-body-medium text-monochrome-1">
                      {map(conflictVoucher.tickets, (ticket) => {
                        return `${ticket.ticket.category.name} - ${ticket.ticket.ticket.name}; `
                      })}
                    </span>
                  </div>
                </div>
                <span className="text-body-medium text-monochrome-1">
                  <strong>General Limit of USES:</strong>{' '}
                  {conflictVoucher.unlimited
                    ? 'Unlimited'
                    : conflictVoucher.voucherLimit}
                </span>
                <span className="text-body-medium text-monochrome-1">
                  <strong>Limit per User:</strong>{' '}
                  {conflictVoucher.userUnlimited
                    ? 'Unlimited'
                    : conflictVoucher.userLimit}
                </span>
              </div>
            </div>
          )}

          <div className="flex gap-3 w-full">
            <FormGroup
              label="Discount Type"
              error={errors?.discountType?.message}
              wrapperClassName="flex-1"
            >
              <Controller
                name="discountType"
                control={control}
                render={({ field }) => (
                  <SelectField
                    outline
                    triggerClassName="h-[44px] rounded-[16px]"
                    value={field.value}
                    onValueChange={field.onChange}
                    options={allDiscountTypes.map((dis) => ({
                      label: dis,
                      value: dis,
                    }))}
                    placeholder="Select Discount Type"
                    error={errors?.discountType?.message}
                    disabled={editMode && !active}
                  />
                )}
              />
            </FormGroup>
            <FormGroup
              label="Discount Amount"
              error={errors?.discountValue?.message}
              wrapperClassName="flex-1"
            >
              <div className="relative w-full">
                <Input
                  isInvalid={!!errors?.discountValue?.message}
                  {...register('discountValue')}
                  type="text"
                  disabled={editMode && !active}
                  placeholder="Select Discount Amount"
                  className="pr-12"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-monochrome-1">
                  {discountType === 'AMOUNT' ? currency : '%'}
                </span>
              </div>
            </FormGroup>
          </div>

          <div className="flex gap-3">
            <FormGroup
              label="Publish Date"
              error={errors?.publishDate?.message}
              wrapperClassName="flex-1"
            >
              <DateTimePicker
                hasError={!!errors?.publishDate?.message}
                value={
                  publishDateWatch ? new Date(publishDateWatch) : undefined
                }
                className="h-[44px] rounded-2xl"
                onChange={(date) => {
                  register('publishDate').onChange({
                    target: {
                      value: date ? formatDateTime(date.valueOf()) : '',
                    },
                  })
                  setValue(
                    'publishDate',
                    date ? formatDateTime(date.valueOf()) : '',
                    {
                      shouldValidate: true,
                    },
                  )
                }}
                placeholder="Enter Publish Date"
                disabled={editMode && !active}
                maxDate={
                  expirationDateWatch
                    ? new Date(expirationDateWatch)
                    : undefined
                }
              />
            </FormGroup>
            <FormGroup
              label="Expiration Date"
              error={errors?.expirationDate?.message}
              wrapperClassName="flex-1"
            >
              <DateTimePicker
                hasError={!!errors?.expirationDate?.message}
                value={
                  expirationDateWatch
                    ? new Date(expirationDateWatch)
                    : undefined
                }
                className="h-[44px] rounded-2xl"
                onChange={(date) => {
                  register('expirationDate').onChange({
                    target: {
                      value: date ? formatDateTime(date.valueOf()) : '',
                    },
                  })
                  setValue(
                    'expirationDate',
                    date ? formatDateTime(date.valueOf()) : '',
                    {
                      shouldValidate: true,
                    },
                  )
                }}
                placeholder="Enter Expiration Date"
                minDate={
                  publishDateWatch ? new Date(publishDateWatch) : undefined
                }
              />
            </FormGroup>
          </div>

          <div className="my-4 h-px w-full bg-monochrome-120" />
          <SectionTitle>Applied Tickets</SectionTitle>

          <div className="flex flex-col gap-3 w-full">
            {map(fields, (field, index) => {
              return (
                <div
                  key={field.id}
                  className="flex flex-col p-3 gap-3 rounded-[10px] border border-monochrome-120 bg-monochrome-5-50"
                >
                  <div className="flex items-end gap-3">
                    <FormGroup
                      label="Applied for Tickets"
                      tooltip={translate(
                        language.value,
                        'rewards.vouchers.appliedForTicketsTooltip',
                      )}
                      wrapperClassName="flex-1"
                      error={errors?.tickets?.[index]?.ticketType?.message}
                    >
                      <>
                        <Controller
                          name={`tickets.${index}.ticketType`}
                          control={control}
                          render={({ field }) => (
                            <SelectField
                              value={field.value || ''}
                              onValueChange={(value) => {
                                field.onChange(value)
                                getCategoryTickets(index)
                              }}
                              triggerClassName="h-[44px] rounded-[16px]"
                              options={ticketCategoriesData.map((tick) => ({
                                label: tick.name,
                                value: tick.id || '',
                              }))}
                              placeholder="Select Ticket"
                              error={
                                errors?.tickets?.[index]?.ticketType?.message
                              }
                              disabled={editMode && !active}
                              outline
                            />
                          )}
                        />
                      </>
                    </FormGroup>
                    <FormGroup
                      label="Select Ticket"
                      tooltip={translate(
                        language.value,
                        'rewards.vouchers.selectTicketsTooltip',
                      )}
                      wrapperClassName="flex-1"
                      error={errors?.tickets?.[index]?.selectedTicket?.message}
                    >
                      <>
                        <Controller
                          name={`tickets.${index}.selectedTicket`}
                          control={control}
                          render={({ field }) => (
                            <SelectField
                              value={field.value || ''}
                              onValueChange={(value) => {
                                field.onChange(value)
                              }}
                              triggerClassName="h-[44px] rounded-[16px]"
                              options={
                                tickets &&
                                tickets[index] &&
                                tickets[index].map((tick) => ({
                                  label: (
                                    <span
                                      className={generateClassBasedOnPublish(
                                        tick,
                                      )}
                                    >
                                      {tick.name} - {tick.price} {currency}
                                    </span>
                                  ),
                                  value: tick.id || '',
                                }))
                              }
                              placeholder="Select Ticket"
                              error={
                                errors?.tickets?.[index]?.selectedTicket
                                  ?.message
                              }
                              disabled={editMode && !active}
                              outline
                            />
                          )}
                        />
                      </>
                    </FormGroup>
                    {fields.length > 0 && (
                      <Button
                        onClick={() => {
                          setDeleteConfirmationModal(true)
                          setRemoveTicket(index)
                        }}
                        disabled={editMode && !active}
                        variant="destructive"
                        type="button"
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                  <FormGroup
                    label="Usage Limit in Bag"
                    error={errors?.tickets?.[index]?.appliesToCount?.message}
                  >
                    <Input
                      isInvalid={
                        !!errors?.tickets?.[index]?.appliesToCount?.message
                      }
                      {...register(`tickets.${index}.appliesToCount`)}
                      type="text"
                      disabled={
                        watch(`tickets.${index}.appliesToCountUnlimited`) ||
                        (editMode && !active)
                      }
                      placeholder="Unlimited"
                    />
                  </FormGroup>
                  <FormGroup label="Unlimited" direction="row">
                    <Controller
                      name={`tickets.${index}.appliesToCountUnlimited`}
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          checked={Boolean(field.value)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setValue(`tickets.${index}.appliesToCount`, null)
                            }
                            return field.onChange(Boolean(checked))
                          }}
                          disabled={editMode && !active}
                        />
                      )}
                    />
                  </FormGroup>
                  {dubaiIntegration && (
                    <>
                      <div className="h-px w-full bg-monochrome-120" />
                      <div>
                        <h5 className="text-xl font-semibold text-monochrome-1 mb-2">
                          Dubai DET Government API
                        </h5>
                        <FormGroup
                          label="overite_prive_type_code:String"
                          error={
                            errors.tickets?.[index]?.dubaiOverwritePriceTypeCode
                              ?.message
                          }
                        >
                          <Input
                            {...register(
                              `tickets.${index}.dubaiOverwritePriceTypeCode`,
                            )}
                            type="text"
                            isInvalid={
                              !!errors.tickets?.[index]
                                ?.dubaiOverwritePriceTypeCode?.message
                            }
                          />
                        </FormGroup>
                      </div>
                    </>
                  )}
                </div>
              )
            })}
          </div>

          {(active || !editMode) && (
            <Button
              variant="secondary"
              size="lg"
              className="w-full"
              onClick={() =>
                append({
                  ticketType: '',
                  selectedTicket: '',
                })
              }
              type="button"
            >
              <span>Add New</span>
              <i className="bi bi-plus-circle" />
            </Button>
          )}

          <div className="my-4 h-px w-full bg-monochrome-120" />

          <FormGroup
            label="General Limit of USES"
            error={errors?.voucherLimit?.message}
          >
            <Input
              isInvalid={!!errors?.voucherLimit?.message}
              {...register('voucherLimit')}
              type="text"
              disabled={unlimited || (editMode && !active)}
              placeholder="General Limit of USES"
            />
          </FormGroup>
          <FormGroup label="Unlimited" direction="row">
            <Controller
              name="unlimited"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                  disabled={editMode && !active}
                />
              )}
            />
          </FormGroup>
          <FormGroup label="Limit per User" error={errors?.userLimit?.message}>
            <Input
              isInvalid={!!errors?.userLimit?.message}
              {...register('userLimit')}
              type="text"
              disabled={userUnlimited || (editMode && !active)}
              placeholder="Limit per User"
            />
          </FormGroup>
          <FormGroup label="Unlimited" direction="row">
            <Controller
              name="userUnlimited"
              control={control}
              render={({ field }) => (
                <Checkbox
                  checked={Boolean(field.value)}
                  onCheckedChange={(checked) =>
                    field.onChange(Boolean(checked))
                  }
                  disabled={editMode && !active}
                />
              )}
            />
          </FormGroup>

          <div className="my-4 h-px w-full bg-monochrome-120" />
          <SectionTitle>Generate QR Codes as</SectionTitle>

          <FormGroup label="">
            <div className="flex flex-col gap-2">
              <FormGroup
                label="SVG"
                direction="row"
                labelClassName="whitespace-nowrap"
                inputWrapperClassName="w-auto"
                wrapperClassName="w-auto flex-row-reverse justify-end"
                labelProps={{
                  htmlFor: 'svg',
                }}
              >
                <Controller
                  name="exportType.svg"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="svg"
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      disabled={editMode && !active}
                    />
                  )}
                />
              </FormGroup>
              <FormGroup
                label="PDF"
                direction="row"
                labelClassName="whitespace-nowrap"
                inputWrapperClassName="w-auto"
                wrapperClassName="w-auto flex-row-reverse justify-end"
                labelProps={{
                  htmlFor: 'pdf',
                }}
              >
                <Controller
                  name="exportType.pdf"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="pdf"
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      disabled={editMode && !active}
                    />
                  )}
                />
              </FormGroup>
              <FormGroup
                label="PNG"
                direction="row"
                labelClassName="whitespace-nowrap"
                inputWrapperClassName="w-auto"
                wrapperClassName="w-auto flex-row-reverse justify-end"
                labelProps={{
                  htmlFor: 'png',
                }}
              >
                <Controller
                  name="exportType.png"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="png"
                      checked={Boolean(field.value)}
                      onCheckedChange={(checked) =>
                        field.onChange(Boolean(checked))
                      }
                      disabled={editMode && !active}
                    />
                  )}
                />
              </FormGroup>
            </div>
          </FormGroup>
        </form>
      </Modal>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Ticket'}
        deleteFunc={deleteFunc}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={deleteModal}
        setDeleteConfirmationModal={setDeleteModal}
        deleteTitle={'Voucher'}
        deleteFunc={deleteVoucher}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
